import { BodyAndContentType, SessionDescriptionHandlerModifier, Web } from "sip.js";
import { SetupTrace } from "./setupTrace";
import { allTurnPathsGathered } from "./iceReadiness";

const AUDIO_OPTIONS: Web.SessionDescriptionHandlerOptions = { constraints: { audio: true, video: false } };

/** Owns the real SIP.js/WebRTC connection while preparing its initial offer in parallel. */
export class PreparedMedia extends Web.SessionDescriptionHandler {
  private initialOffer?: Promise<BodyAndContentType>;
  private closed = false;
  private trace: SetupTrace = () => {};
  private preparingInitial = false;
  private lastCandidateAt = 0;

  setTrace(trace: SetupTrace): void {
    this.trace = trace;
    const seen = new Set<string>();
    this.peerConnectionDelegate = {
      onicecandidate: event => {
        if (event.candidate) this.lastCandidateAt = performance.now();
        const type = event.candidate?.type;
        const protocol = event.candidate?.protocol;
        const label = `${type || "complete"}/${protocol || ""}`;
        if (!seen.has(label)) { seen.add(label); trace("ice-candidate", label); }
      },
      onicecandidateerror: event => trace("ice-error", String(event.errorCode)),
      onicegatheringstatechange: () => trace("ice-state", this.peerConnection?.iceGatheringState),
      onconnectionstatechange: () => trace("media-state", this.peerConnection?.connectionState)
    };
  }

  prepare(): Promise<void> {
    if (this.closed) return Promise.reject(new DOMException("Call cancelled", "AbortError"));
    if (!this.initialOffer) {
      this.trace("offer-start");
      this.preparingInitial = true;
      this.initialOffer = super.getDescription(AUDIO_OPTIONS, []).then(description => {
        if (this.closed) throw new DOMException("Call cancelled", "AbortError");
        if (!/^a=candidate:/m.test(description.body)) throw new Error("No ICE candidates gathered");
        this.trace("offer-ready", `candidates=${(description.body.match(/^a=candidate:/gm) || []).length}`);
        return description;
      }).finally(() => { this.preparingInitial = false; });
    }
    return this.initialOffer.then(() => undefined);
  }

  getDescription(options?: Web.SessionDescriptionHandlerOptions, modifiers?: SessionDescriptionHandlerModifier[]): Promise<BodyAndContentType> {
    if (this.closed) return Promise.reject(new DOMException("Call cancelled", "AbortError"));
    if (this.initialOffer) {
      const offer = this.initialOffer;
      this.initialOffer = undefined; // Re-INVITEs must generate fresh SDP through SIP.js.
      return offer.then(description => {
        if (this.closed) throw new DOMException("Call cancelled", "AbortError");
        return description;
      });
    }
    return super.getDescription(options, modifiers);
  }

  protected async getLocalMediaStream(options?: Web.SessionDescriptionHandlerOptions): Promise<void> {
    this.trace("microphone-start");
    await super.getLocalMediaStream(options);
    this.trace("microphone-ready");
  }

  protected setLocalMediaStream(stream: MediaStream): Promise<void> {
    // getUserMedia permission prompts cannot be aborted. Stop any stream arriving after cancel.
    if (this.closed) {
      stream.getTracks().forEach(track => track.stop());
      return Promise.reject(new DOMException("Call cancelled", "AbortError"));
    }
    return super.setLocalMediaStream(stream);
  }

  protected async waitForIceGatheringComplete(restart = false, timeout = 0): Promise<void> {
    this.trace("ice-wait-start");
    let active = this.preparingInitial && !restart;
    let timer: number | undefined;
    const checkPaths = async () => {
      const peer = this.peerConnection;
      if (!active || !peer || this.closed) return;
      try {
        const report = await peer.getStats();
        const candidates: Array<{ type?: string; candidateType?: string; url?: string; relayProtocol?: string }> = [];
        report.forEach(stat => { if (stat.type === "local-candidate") candidates.push(stat); });
        if (active && !this.closed && performance.now() - this.lastCandidateAt >= 200 &&
            allTurnPathsGathered(peer.getConfiguration().iceServers || [], candidates, peer.localDescription?.sdp || "")) {
          this.trace("ice-all-turn-paths-ready");
          this.iceGatheringComplete();
          return;
        }
      } catch { /* Missing candidate stats or a closing connection use the SDK's normal deadline. */ }
      if (active && !this.closed) timer = window.setTimeout(checkPaths, 100);
    };
    try {
      const complete = super.waitForIceGatheringComplete(restart, timeout);
      if (active) timer = window.setTimeout(checkPaths, 100);
      await complete;
      this.trace("ice-wait-end", this.peerConnection?.iceGatheringState);
    } finally { active = false; window.clearTimeout(timer); }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    super.close();
    this.iceGatheringComplete(); // Release SIP.js's pending ICE wait/timer on cancellation.
    this.trace("media-closed");
  }
}
